import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InboxTabs from '../../components/admin/InboxTabs';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Inbox, ArrowUpRight, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { KIND_LABELS, STATUS_LABELS } from '../../components/crm/RequestsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [['open', 'Open'], ['approved_awaiting_scheduling', 'Awaiting scheduling'], ['approved', 'Approved'], ['declined', 'Declined'], ['resolved', 'Resolved'], ['', 'All']];
const PRIORITY_TONE = { urgent: 'bg-red-100 text-red-700', high: 'bg-[#fff0e4] text-[#b75b28]', normal: 'bg-[#edf0e6] text-[#6e7a55]', low: 'bg-zinc-100 text-zinc-500' };
const FINAL = ['approved_awaiting_scheduling', 'approved', 'declined', 'resolved'];
const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const ReviewDetail = ({ id, headers, owners, onChanged, onClose }) => {
  const [item, setItem] = useState(null);
  const [note, setNote] = useState('');
  const [response, setResponse] = useState('');
  const [decision, setDecision] = useState('approved_awaiting_scheduling');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API_URL}/api/admin/review-items/${id}`, { headers }).then((r) => setItem(r.data)).catch(() => toast.error('Could not load this review')), [id, headers]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); await load(); window.dispatchEvent(new Event('gg-review-changed')); } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — please try again.');
      if (e.response?.status === 409) load();
    } finally { setBusy(false); }
  };
  const patch = (body, ok = 'Saved') => run(() => axios.patch(`${API_URL}/api/admin/review-items/${id}`, { version: item.version, ...body }, { headers }), ok);
  if (!item) return <div className="p-8"><Loader2 className="h-5 w-5 animate-spin text-[#F56F05]" /></div>;
  const final = FINAL.includes(item.status);
  const isOutcome = item.kind === 'job_exception';
  const legacy = !!item.legacy_change_request_id;
  const choices = legacy ? ['approved', 'declined'] : FINAL;
  const picked = choices.includes(decision) ? decision : choices[0];

  return (
    <div className="p-6 space-y-5" data-testid="review-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">{KIND_LABELS[item.kind]?.toUpperCase()}</p>
          <h2 className="text-2xl font-semibold tracking-tight text-[#20211d] mt-1">{item.title}</h2>
          <p className="text-xs text-[#77776e] mt-1">{item.requester_name} ({item.requester_role}) · {fmt(item.created_at)}</p>
        </div>
        <button onClick={onClose} className="p-2 text-[#77776e] hover:text-black" aria-label="Close" data-testid="review-detail-close"><X className="h-4 w-4" /></button>
      </div>
      {item.details && <p className="text-sm text-[#4d5740] whitespace-pre-wrap border-l-2 border-[#e0ac7b] pl-3">{item.details}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div className="rounded-lg bg-white border border-[#e7e4db] p-3"><p className="text-[#919784] tracking-wider text-[9px] font-bold">CLIENT</p>
          {item.client ? <Link className="text-[#b64e13] font-semibold" to={`/admin/customers/${item.client.id}`} data-testid="review-client-link">{item.client.full_name}</Link> : '—'}</div>
        <div className="rounded-lg bg-white border border-[#e7e4db] p-3"><p className="text-[#919784] tracking-wider text-[9px] font-bold">PROPERTY</p>{item.property ? [item.property.line1, item.property.city].filter(Boolean).join(', ') : '—'}</div>
        <div className="rounded-lg bg-white border border-[#e7e4db] p-3"><p className="text-[#919784] tracking-wider text-[9px] font-bold">VISIT</p>
          {item.appointment ? <Link className="text-[#b64e13] font-semibold" to={`/admin/appointments/${item.appointment.id}`} data-testid="review-visit-link">{fmt(item.appointment.start_time)} · {item.appointment.status}</Link> : '—'}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Owner</Label>
          <Select value={item.owner_id || ''} onValueChange={(v) => patch({ owner_id: v }, 'Owner set')} disabled={busy || final}>
            <SelectTrigger data-testid="review-owner"><SelectValue placeholder="Assign owner" /></SelectTrigger>
            <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Priority</Label>
          <Select value={item.priority} onValueChange={(v) => patch({ priority: v })} disabled={busy || final}>
            <SelectTrigger data-testid="review-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{['low', 'normal', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Follow-up date</Label>
          <Input type="date" defaultValue={item.follow_up_date || ''} key={item.follow_up_date || 'none'} onBlur={(e) => e.target.value !== (item.follow_up_date || '') && patch({ follow_up_date: e.target.value })} disabled={busy || final} data-testid="review-follow-up" /></div>
      </div>
      {!final && (
        <div className="flex gap-2 flex-wrap">
          {[['in_review', 'Mark in review'], ['waiting', 'Waiting on details']].map(([s, l]) => (
            <Button key={s} size="sm" variant="outline" disabled={busy || item.status === s} onClick={() => patch({ status: s })} data-testid={`review-status-${s}`}>{l}</Button>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-[#e7e4db] bg-white p-4">
        <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">INTERNAL NOTES · STAFF ONLY</p>
        {(item.internal_notes || []).map((n) => <p key={n.id} className="text-sm mt-2"><b>{n.author_name}</b> <span className="text-[11px] text-[#8c8e82]">{fmt(n.at)}</span><br />{n.text}</p>)}
        <div className="flex gap-2 mt-3">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note" data-testid="review-note-input" />
          <Button size="sm" variant="outline" disabled={busy || !note.trim()} onClick={() => patch({ internal_note: note }, 'Note added').then(() => setNote(''))} data-testid="review-note-add">Add</Button>
        </div>
      </div>

      {final ? (
        <div className="rounded-xl bg-[#e9eddf] border border-[#dbe1ce] p-4 text-sm" data-testid="review-final">
          <p className="font-semibold text-[#536543]">{STATUS_LABELS[item.status]} · {item.decision?.by_name} · {fmt(item.decided_at)}</p>
          <p className="mt-1 text-[#4d5740]">Response sent to requester: {item.response}</p>
          {item.status === 'approved_awaiting_scheduling' && <p className="text-[11px] mt-2 text-[#788064]">Not booked yet. The existing visit stays as it is until a scheduling change is saved from the schedule.</p>}
        </div>
      ) : (
        <div className="rounded-xl border border-[#e7e4db] bg-white p-4 space-y-3">
          <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">DECISION</p>
          <Select value={picked} onValueChange={setDecision}>
            <SelectTrigger data-testid="review-decision-select"><SelectValue /></SelectTrigger>
            <SelectContent>{choices.map((d) => <SelectItem key={d} value={d}>{STATUS_LABELS[d]}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={3} value={response} onChange={(e) => setResponse(e.target.value)} placeholder="Response the requester will see" data-testid="review-response" />
          {!item.owner_id && <p className="text-xs text-amber-700">Assign an owner before recording a decision.</p>}
          <p className="text-[11px] text-[#788064]" data-testid="review-decision-note">{legacy
            ? `Change request ${item.legacy_change_request_id.slice(0, 8)} from the original Change Requests workflow. Approving applies the requested change exactly as it did there; declining leaves the visit unchanged.`
            : 'Recording a decision never charges, refunds, reschedules or changes hours by itself.'}</p>
          <Button className="bg-[#252a20] hover:bg-black text-white" disabled={busy || !item.owner_id || response.trim().length < 3}
            onClick={() => run(() => axios.post(`${API_URL}/api/admin/review-items/${id}/decision`, { version: item.version, decision: picked, response }, { headers }), 'Decision recorded')} data-testid="review-decision-submit">
            Record decision
          </Button>
        </div>
      )}

      {isOutcome && item.appointment && (
        <div className="rounded-xl border border-[#e7e4db] bg-white p-4 text-sm">
          <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">VISIT HOLD</p>
          <p className="text-[#5f6455] mt-1">The visit is held after the Guru's report. Authorize a resume only when it's safe and agreed.</p>
          <Button size="sm" variant="outline" className="mt-2" disabled={busy || (item.linked_actions || []).some((l) => l.type === 'resume_authorized')}
            onClick={() => run(() => axios.post(`${API_URL}/api/admin/review-items/${id}/link`, { version: item.version, action_type: 'resume_authorized', reference_id: item.appointment.id }, { headers }), 'Resume authorized')} data-testid="review-authorize-resume">
            Authorize resume
          </Button>
        </div>
      )}
      {(item.linked_actions || []).length > 0 && (
        <div className="text-xs text-[#5f6455]"><p className="font-bold mb-1">Linked actions</p>{item.linked_actions.map((l) => <p key={l.id}>{l.type.replace(/_/g, ' ')} · {l.by_name} · {fmt(l.at)}</p>)}</div>
      )}
      <div>
        <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d] mb-2">ACTIVITY</p>
        <ol className="space-y-2" data-testid="review-activity">{(item.activity || []).slice().reverse().map((a) => (
          <li key={a.id} className="text-xs text-[#5f6455]"><span className="text-[#8c8e82]">{fmt(a.at)}</span> · {a.actor_name}: {a.action.replace(/_/g, ' ')}{a.text ? ` — ${a.text}` : ''}{a.requester_visible ? ' (visible to requester)' : ''}</li>
        ))}</ol>
      </div>
    </div>
  );
};

export default function AdminNeedsReviewPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('open');
  const [items, setItems] = useState(null);
  const [owners, setOwners] = useState([]);
  const [summary, setSummary] = useState(null);
  const selected = params.get('item');

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/admin/review-items${filter ? `?status=${filter}` : ''}`, { headers }).then((r) => setItems(r.data)).catch(() => setItems([]));
    axios.get(`${API_URL}/api/admin/review-items/summary`, { headers }).then((r) => setSummary(r.data)).catch(() => {});
  }, [filter, headers]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API_URL}/api/admin/review-owners`, { headers }).then((r) => setOwners(r.data)).catch(() => {}); }, [headers]);

  return (
    <div className="min-h-screen bg-[#f4f2ed]" data-testid="needs-review-page">
      <InboxTabs />
      <main className="max-w-[1400px] mx-auto px-5 sm:px-9 py-8">
        <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]" data-legacy-title>INBOX</p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#20211d] mt-1" data-legacy-title>Needs Review<span className="text-[#939681] font-normal">.</span></h1>
        <p className="text-sm text-[#77776e] mt-2"><b className="text-[#20211d]">Needs Review · </b>Client and Guru requests, visit outcomes and reports, each linked to its record.</p>
        {summary?.pending_change_requests > 0 && (
          <Link to="/admin/change-requests" className="mt-4 inline-flex items-center gap-1 text-xs text-[#5f6455]" data-testid="legacy-change-requests-link">
            {summary.pending_change_requests} reschedule/cancel request(s) from the original Change Requests page are included below <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
        <div className="flex gap-2 flex-wrap mt-6" role="tablist">
          {FILTERS.map(([v, l]) => (
            <button key={l} role="tab" aria-selected={filter === v} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${filter === v ? 'bg-[#252a20] text-white border-[#252a20]' : 'bg-[#fbfaf7] border-[#dcded2] text-[#5f6455]'}`} data-testid={`review-filter-${l.toLowerCase().replace(/\s+/g, '-')}`}>{l}{v === 'open' && summary ? ` · ${summary.open}` : ''}</button>
          ))}
        </div>
        <div className={`mt-5 grid gap-5 ${selected ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]' : ''}`}>
          <div className="rounded-xl border border-[#e7e4db] bg-[#fffefa] divide-y divide-[#e7e4db]" data-testid="review-list">
            {items === null ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-[#F56F05]" /></div> : !items.length ? (
              <div className="p-10 text-center text-sm text-[#77776e]" data-testid="review-empty"><Inbox className="h-6 w-6 mx-auto mb-2 text-[#b4b8a6]" />Nothing here right now.</div>
            ) : items.map((it) => (
              <button key={it.id} onClick={() => setParams({ item: it.id })} className={`w-full text-left p-4 hover:bg-[#f6f7ee] transition-colors ${selected === it.id ? 'bg-[#eeefe5]' : ''}`} data-testid={`review-row-${it.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${PRIORITY_TONE[it.priority]}`}>{it.priority}</span>
                  <span className="text-[10px] tracking-wider font-bold text-[#858a77]">{KIND_LABELS[it.kind]?.toUpperCase()}</span>
                  <span className="ml-auto text-[11px] text-[#8c8e82]">{fmt(it.created_at)}</span>
                </div>
                <p className="text-sm font-semibold text-[#20211d] mt-1.5">{it.title}</p>
                <p className="text-[11px] text-[#77776e] mt-0.5">{it.requester_name} · {STATUS_LABELS[it.status]}{it.owner_name ? ` · Owner: ${it.owner_name}` : ' · No owner'}{it.follow_up_date ? ` · Follow up ${it.follow_up_date}` : ''}</p>
              </button>
            ))}
          </div>
          {selected && (
            <div className="rounded-xl border border-[#e7e4db] bg-[#faf9f5]">
              <ReviewDetail key={selected} id={selected} headers={headers} owners={owners} onChanged={load} onClose={() => setParams({})} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
